import React, { useEffect, useMemo, useState } from "react";
import { FaSearch, FaSpinner, FaTrash, FaUserMinus, FaUserPlus } from "react-icons/fa";
import Modal from "../modal/modal";
import useApp from "../../hooks/useAppContext";

const FILTRO_TIPO_TODOS = "todos";

const LABEL_TIPO_USUARIO = {
  convidado: "Convidado",
  patrocinador: "Patrocinador",
  participante_patrocinador: "Participante patrocinador",
  administracao: "Administração",
  administracao_dash: "Admin. dashboard",
  recepcao: "Recepção",
};

function labelTipoUsuario(tipo) {
  if (!tipo) return "Sem tipo";
  return LABEL_TIPO_USUARIO[tipo] || tipo;
}

function metaCargoEmpresa(usuario) {
  const cargo = String(usuario?.cargo || "").trim();
  const empresa = String(usuario?.empresa || "").trim();
  if (cargo && empresa) return `${cargo} / ${empresa}`;
  return cargo || empresa || "";
}

/**
 * Modal reutilizável de inscritos (agenda geral e conteúdos/esportes).
 * Badge: foto | nome + cargo/empresa | remover
 */
export default function ModalInscritosAgenda({
  show,
  onClose,
  titulo,
  participantes = [],
  imprimirTo,
  isAdmin = false,
  loading = false,
  onRemoverParticipante,
  onAdicionarTodos,
  onExcluirTodos,
  onIncluirPatrocinadoresCaviar,
  emptyHintSemInscritos = "Nenhum inscrito nesta agenda.",
}) {
  const { handleImagemParticipante } = useApp();
  const [filtro, setFiltro] = useState("");
  const [filtroTipo, setFiltroTipo] = useState(FILTRO_TIPO_TODOS);

  const tiposDisponiveis = useMemo(() => {
    const contagem = new Map();
    for (const p of participantes) {
      const tipo = p?.usuario?.tipo || "";
      contagem.set(tipo, (contagem.get(tipo) || 0) + 1);
    }

    return Array.from(contagem.entries())
      .map(([tipo, quantidade]) => ({
        tipo,
        quantidade,
        label: labelTipoUsuario(tipo),
      }))
      .sort((a, b) => {
        if (a.tipo === "convidado") return -1;
        if (b.tipo === "convidado") return 1;
        return a.label.localeCompare(b.label, "pt-BR");
      });
  }, [participantes]);

  useEffect(() => {
    if (filtroTipo === FILTRO_TIPO_TODOS) return;
    const aindaExiste = tiposDisponiveis.some((t) => t.tipo === filtroTipo);
    if (!aindaExiste) setFiltroTipo(FILTRO_TIPO_TODOS);
  }, [tiposDisponiveis, filtroTipo]);

  const filtrados = useMemo(() => {
    const termo = filtro.toLowerCase().trim();
    return participantes.filter((p) => {
      const tipo = p?.usuario?.tipo || "";
      if (filtroTipo !== FILTRO_TIPO_TODOS && tipo !== filtroTipo) return false;
      if (!termo) return true;
      const campos = [
        p?.usuario?.nome,
        p?.usuario?.empresa,
        p?.usuario?.cargo,
        p?.usuario?.tipo,
        labelTipoUsuario(tipo),
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [participantes, filtro, filtroTipo]);

  const handleClose = () => {
    setFiltro("");
    setFiltroTipo(FILTRO_TIPO_TODOS);
    onClose?.();
  };

  const footerContent = (
    <>
      <div
        className="agenda-inscritos-legenda"
        aria-label="Legenda dos tipos de inscrito"
      >
        <span className="agenda-inscritos-legenda-item">
          <span
            className="agenda-inscritos-legenda-swatch agenda-inscritos-legenda-swatch--convidado"
            aria-hidden
          />
          Convidado
        </span>
        <span className="agenda-inscritos-legenda-item">
          <span
            className="agenda-inscritos-legenda-swatch agenda-inscritos-legenda-swatch--outros"
            aria-hidden
          />
          Outros
        </span>
      </div>
      {isAdmin && onIncluirPatrocinadoresCaviar ? (
        <button
          type="button"
          className="apl-btn apl-btn--primary"
          onClick={onIncluirPatrocinadoresCaviar}
          disabled={loading}
          title="Incluir participantes patrocinadores com interesse no caviar"
        >
          {loading ? (
            <FaSpinner className="fa-spin" size={14} aria-hidden />
          ) : (
            <FaUserPlus size={14} aria-hidden />
          )}
          Incluir patrocinadores
        </button>
      ) : null}
      {isAdmin && onAdicionarTodos ? (
        <button
          type="button"
          className="apl-btn apl-btn--primary"
          onClick={onAdicionarTodos}
          disabled={loading}
        >
          {loading ? (
            <FaSpinner className="fa-spin" size={14} aria-hidden />
          ) : (
            <FaUserPlus size={14} aria-hidden />
          )}
          Adicionar a todos
        </button>
      ) : null}
      {isAdmin && onExcluirTodos ? (
        <button
          type="button"
          className="apl-btn apl-btn--secondary"
          onClick={onExcluirTodos}
          disabled={loading || participantes.length === 0}
        >
          <FaUserMinus size={14} aria-hidden />
          Excluir todos
        </button>
      ) : null}
    </>
  );

  return (
    <Modal
      titulo={titulo}
      show={show}
      toggleModal={handleClose}
      to={imprimirTo || undefined}
      labelTo="Imprimir"
      size="lg"
      dialogClassName="modal-inscritos-agenda"
      footerClassName="agenda-inscritos-footer"
      footerContent={footerContent}
    >
      <div className="agenda-inscritos-modal">
        <div className="agenda-inscritos-toolbar">
          <div className="agenda-inscritos-toolbar-busca">
            <div className="apl-search agenda-inscritos-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por nome, cargo ou empresa…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={loading}
                aria-label="Buscar inscrito por nome, cargo ou empresa"
              />
            </div>
            <span className="apl-stat-muted agenda-inscritos-contador">
              {filtrados.length} de {participantes.length}
            </span>
          </div>

          {tiposDisponiveis.length > 0 ? (
            <div
              className="agenda-inscritos-tipo-filtros"
              role="group"
              aria-label="Filtrar por tipo de inscrito"
            >
              <button
                type="button"
                className={`agenda-inscritos-tipo-btn${
                  filtroTipo === FILTRO_TIPO_TODOS ? " is-active" : ""
                }`}
                onClick={() => setFiltroTipo(FILTRO_TIPO_TODOS)}
                disabled={loading}
              >
                Todos
                <span className="agenda-inscritos-tipo-count">
                  {participantes.length}
                </span>
              </button>
              {tiposDisponiveis.map((item) => {
                const isConvidado = item.tipo === "convidado";
                return (
                  <button
                    key={item.tipo || "sem-tipo"}
                    type="button"
                    className={`agenda-inscritos-tipo-btn${
                      isConvidado
                        ? " agenda-inscritos-tipo-btn--convidado"
                        : " agenda-inscritos-tipo-btn--outros"
                    }${filtroTipo === item.tipo ? " is-active" : ""}`}
                    onClick={() => setFiltroTipo(item.tipo)}
                    disabled={loading}
                  >
                    {item.label}
                    <span className="agenda-inscritos-tipo-count">
                      {item.quantidade}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {filtrados.length === 0 ? (
          <div className="apl-empty" style={{ padding: "2rem 1rem" }}>
            <p className="apl-empty-title">
              {participantes.length === 0
                ? "Nenhum inscrito nesta agenda"
                : "Nenhum resultado"}
            </p>
            <p style={{ margin: 0 }}>
              {participantes.length === 0
                ? emptyHintSemInscritos
                : "Ajuste a busca ou o filtro de tipo para ver inscritos."}
            </p>
          </div>
        ) : (
          <div className="agenda-inscritos-lista">
            {filtrados.map((ap) => {
              const usuario = ap.usuario || {};
              const tipo = usuario.tipo;
              const isConvidado = tipo === "convidado";
              const meta = metaCargoEmpresa(usuario);

              return (
                <div
                  className={`agenda-inscritos-badge${
                    isConvidado
                      ? " agenda-inscritos-badge--convidado"
                      : " agenda-inscritos-badge--outros"
                  }`}
                  key={ap.id || usuario.id}
                >
                  <img
                    src={handleImagemParticipante(usuario)}
                    alt=""
                    className="agenda-inscritos-badge-foto"
                  />
                  <div className="agenda-inscritos-badge-texto">
                    <span className="agenda-inscritos-badge-nome">
                      {usuario.nome || "—"}
                    </span>
                    {meta ? (
                      <span className="agenda-inscritos-badge-meta">{meta}</span>
                    ) : null}
                  </div>
                  {isAdmin && onRemoverParticipante ? (
                    <button
                      type="button"
                      className="agenda-inscritos-badge-remover"
                      onClick={() => onRemoverParticipante(ap)}
                      disabled={loading}
                      title="Remover participante"
                      aria-label={`Remover ${usuario.nome || "participante"}`}
                    >
                      <FaTrash size={10} aria-hidden />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
